import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { ID_LIST_LIMIT, joinIds } from "@restomenu/core/contract";
import { api } from "@/core/api-client";

/**
 * Режет список на пачки не длиннее лимита `ids` у /guest/images.
 *
 * useImageUrls из Ф3 переиспользовать нельзя: он делает slice(0, 100) —
 * для админской таблицы с пагинацией это приемлемо, а для меню, где
 * картинка есть у каждого товара и каждой категории, это молчаливая
 * потеря начиная со 101-й.
 */
export function chunkIds(
  ids: readonly string[],
  size: number = ID_LIST_LIMIT,
): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/**
 * Карта id → url для обложек меню.
 *
 * Связи в гостевом ответе выражаются идентификаторами: у товара
 * `imageBinds[]`, у категории `imageBind`, а URL читается отдельным
 * запросом.
 */
export function useGuestImageUrls(
  pointId: string | null,
  imageIds: readonly (string | null | undefined)[],
): Record<string, string> {
  const chunks = useMemo(() => {
    const unique = Array.from(
      new Set(imageIds.filter((id): id is string => Boolean(id))),
    ).sort();
    return chunkIds(unique);
  }, [imageIds]);

  // useQueries, а не useQuery в цикле: число пачек переменное, и цикл
  // нарушил бы rules of hooks.
  //
  // combine обязателен. Без него useQueries отдаёт НОВЫЙ массив на каждый
  // рендер, useMemo по нему не срабатывает никогда, и полный маппинг меню
  // пересчитывался бы на каждом рендере каждого потребителя useMenu — там,
  // где раньше был один общий createSelector.
  return useQueries({
    queries: chunks.map((chunk) =>
      api.queryOptions(
        "get",
        "/guest/images",
        {
          params: {
            query: { pointId: pointId ?? "", ids: joinIds(chunk) },
          },
        },
        { enabled: Boolean(pointId) && chunk.length > 0 },
      ),
    ),
    combine: (results) => {
      // Пачки складываем по мере готовности: частично приехавшие картинки
      // лучше показать, чем ждать последнюю.
      const map: Record<string, string> = {};
      for (const result of results) {
        for (const image of result.data ?? []) {
          map[image.id] = image.url;
        }
      }
      return map;
    },
  });
}
